import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Radio, Star, Clock, ListMusic, Search, Play, RefreshCw, LogOut, Plus,
  Trash2, Globe, Loader2, X, Tv, Film, Filter, Eye, Languages, Music2, Pencil,
} from "lucide-react";
import { Logo } from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import AudioPlayer from "../components/AudioPlayer";
import { api, errMsg, logoUrl, ensureMediaToken, clearMediaToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFavorites, useRecent } from "../hooks/useLocalData";

const DIRECTORY = { id: "__directory__", name: "Global Directory · 50k+ stations", url: "__directory__", is_global: true, kind: "directory" };
// Every playlist the user can see, merged by the Worker into one browsable set.
const ALL_LISTS = { id: "__all__", name: "All playlists · everything combined", url: "__all__", is_global: true, kind: "all" };

// Mirrors splitGenres in the Worker: an M3U group-title is a comma-separated tag
// list, so a station tagged "80s,90s,trance" must match each of those filters.
const splitGenres = (raw) => [
  ...new Set(
    String(raw || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t && t !== "uncategorized")
      .map((t) => t.replace(/\b\w/g, (c) => c.toUpperCase()))
  ),
];

const PRESETS = [
  { name: "Jazz", url: "https://raw.githubusercontent.com/junguler/m3u-radio-music-playlists/main/jazz.m3u" },
  { name: "Classical", url: "https://raw.githubusercontent.com/junguler/m3u-radio-music-playlists/main/classical.m3u" },
  { name: "Rock", url: "https://raw.githubusercontent.com/junguler/m3u-radio-music-playlists/main/rock.m3u" },
  { name: "Pop", url: "https://raw.githubusercontent.com/junguler/m3u-radio-music-playlists/main/pop.m3u" },
  { name: "Ambient", url: "https://raw.githubusercontent.com/junguler/m3u-radio-music-playlists/main/ambient.m3u" },
  { name: "EDM", url: "https://raw.githubusercontent.com/junguler/m3u-radio-music-playlists/main/edm.m3u" },
  { name: "Hip-Hop", url: "https://raw.githubusercontent.com/junguler/m3u-radio-music-playlists/main/hip-hop.m3u" },
  { name: "Metal", url: "https://raw.githubusercontent.com/junguler/m3u-radio-music-playlists/main/metal.m3u" },
];

const NAV = [
  { id: "browse", label: "Browse", icon: Radio },
  { id: "favorites", label: "Favourites", icon: Star },
  { id: "recent", label: "Recently Played", icon: Clock },
  { id: "lists", label: "My Lists", icon: ListMusic },
];

export default function Player() {
  const nav = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const previewMode = !user && isAdmin;
  const email = user?.email || (previewMode ? "admin@preview.local" : "guest");
  const { favs, isFav, toggle } = useFavorites(email);
  const { recent, push, clear } = useRecent(email);

  const [page, setPage] = useState("browse");
  const [lists, setLists] = useState([]);
  const [activeUrl, setActiveUrl] = useState(DIRECTORY.url);
  const [data, setData] = useState({ stations: [], genres: [], countries: [], languages: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [playing, setPlaying] = useState(null);

  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("All");
  const [country, setCountry] = useState("All");
  const [language, setLanguage] = useState("All");

  const loadLists = useCallback(async () => {
    try {
      const { data } = await api.get("/lists");
      setLists(data.lists);
      return data.lists;
    } catch (e) { setErr(errMsg(e)); return []; }
  }, []);

  const loadStations = useCallback(async (url) => {
    if (!url) return;
    setActiveUrl(url);
    setLoading(true);
    setErr("");
    setQ(""); setGenre("All"); setCountry("All"); setLanguage("All");
    try {
      const endpoint =
        url === DIRECTORY.url ? "/stations"
        : url === ALL_LISTS.url ? "/radio/parse-all"
        : `/radio/parse?url=${encodeURIComponent(url)}`;
      const { data } = await api.get(endpoint);
      setData(data);
    } catch (e) {
      setErr(errMsg(e));
      setData({ stations: [], genres: [], countries: [], languages: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Must resolve before any station renders: logo and stream URLs embed this token,
    // and one built without it would 401 on the proxy.
    ensureMediaToken().then(() => {
      loadLists();
      loadStations(DIRECTORY.url);
    });
  }, [loadLists, loadStations]);

  const playStation = (s) => { setPlaying(s); push(s); };
  const doLogout = () => {
    if (previewMode) { nav("/admin"); return; }
    clearMediaToken();
    logout();
    nav("/login");
  };

  // "All playlists" only makes sense once there is more than one to combine.
  const allSources = useMemo(
    () => (lists.length > 1 ? [DIRECTORY, ALL_LISTS, ...lists] : [DIRECTORY, ...lists]),
    [lists]
  );

  const filtered = useMemo(() => {
    let list = page === "favorites" ? favs : page === "recent" ? recent : data.stations;
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(s) || (c.genre || "").toLowerCase().includes(s) || (c.country || "").toLowerCase().includes(s) || (c.language || "").toLowerCase().includes(s));
    }
    if (genre !== "All") list = list.filter((c) => splitGenres(c.genre).includes(genre));
    if (country !== "All") list = list.filter((c) => c.country === country);
    if (language !== "All") list = list.filter((c) => c.language === language);
    return list;
  }, [page, favs, recent, data.stations, q, genre, country, language]);

  const activeFilters = [genre, country, language].filter((v) => v !== "All").length;

  return (
    <div className="min-h-screen bg-rv-darkest flex text-rv-text pb-24">
      {/* Sidebar */}
      <aside className="w-[220px] shrink-0 border-r border-rv-border bg-rv-darkest flex flex-col fixed h-screen z-30">
        <div className="px-5 pt-5 pb-3"><Logo size={21} /></div>
        <nav className="mt-3 flex-1">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setPage(n.id)}
              data-testid={`nav-${n.id}`}
              className={`w-full flex items-center gap-3 px-5 py-3 text-sm font-semibold transition-colors ${
                page === n.id ? "bg-rv-card text-rv-cyan border-l-[3px] border-rv-cyan pl-[17px]" : "text-rv-sub hover:bg-rv-hover hover:text-rv-text"
              }`}
            >
              <n.icon size={18} />
              {n.label}
              {n.id === "favorites" && favs.length > 0 && (
                <span className="ml-auto text-[10px] bg-rv-active text-rv-cyan px-2 py-0.5 rounded-full">{favs.length}</span>
              )}
            </button>
          ))}

          <a
            href="https://mediaverselabs.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block px-5 mt-6 mb-2 text-[9px] uppercase tracking-[2.5px] text-rv-muted font-bold hover:text-rv-cyan transition-colors"
            data-testid="eco-header-link"
          >
            Ecosystem
          </a>
          <div className="px-5 space-y-2">
            <EcoItem icon={Radio} color="#8B5CF6" label="RadioVerse" note="Active" active />
            <EcoItem icon={Tv} color="#22D3EE" label="TVVerse" note="Live" active href="https://tvverse.mediaverselabs.com" />
            <EcoItem icon={Film} color="#FB923C" label="ClassicVerse" note="Soon" href="https://mediaverselabs.com" />
          </div>
        </nav>

        <div className="p-4 border-t border-rv-border">
          <div className="flex items-center gap-2.5 mb-3 px-1">
            <div className="w-8 h-8 rounded-full rv-gradient flex items-center justify-center text-white font-extrabold text-sm">
              {(user?.username || email)[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.username || (previewMode ? "Admin (preview)" : "You")}</div>
              <div className="text-[11px] text-rv-muted truncate">{email}</div>
            </div>
          </div>
          <button onClick={doLogout} className="btn-ghost w-full flex items-center justify-center gap-2 text-sm" data-testid="logout-button">
            <LogOut size={15} /> {previewMode ? "Back to admin" : "Log out"}
          </button>
          <ThemeToggle className="w-full mt-2" />
          <div className="text-[10px] text-rv-muted text-center mt-3">
            v1.0 ·{" "}
            <a href="https://mediaverselabs.com" target="_blank" rel="noopener noreferrer" className="hover:text-rv-cyan transition-colors" data-testid="footer-mvl-link">
              © 2026 MediaVerse Labs
            </a>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-[220px] p-8 min-h-screen">
        {previewMode && (
          <div className="mb-5 flex items-center justify-between bg-rv-orange/10 border border-rv-orange/30 rounded-xl px-4 py-3" data-testid="preview-banner">
            <span className="text-sm text-rv-orange font-semibold flex items-center gap-2">
              <Eye size={16} /> Preview mode: you're seeing the app as a regular user
            </span>
            <button onClick={() => nav("/admin")} className="btn-ghost text-sm py-1.5" data-testid="exit-preview-button">Back to admin</button>
          </div>
        )}
        {err && (
          <div className="mb-5 text-sm text-rv-red bg-rv-red/10 border border-rv-red/30 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="player-error-banner">
            <span>{err}</span>
            <button onClick={() => setErr("")}><X size={15} /></button>
          </div>
        )}

        {page === "lists" ? (
          <MyLists lists={lists} reload={loadLists} onActivate={(url) => { setPage("browse"); loadStations(url); }} activeUrl={activeUrl} />
        ) : (
          <>
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-extrabold tracking-tight">
                  {page === "browse" ? "Browse Radio" : page === "favorites" ? "My Favourites" : "Recently Played"}
                </h1>
                <p className="text-rv-sub text-sm mt-1">
                  {page === "browse"
                    ? `${(data.stations || []).length.toLocaleString()} stations` +
                      (data.sources ? ` from ${data.sources} playlists` : "")
                    : page === "favorites"
                    ? `${favs.length} saved stations`
                    : `${recent.length} recently played`}
                </p>
                {page === "browse" && data.failed?.length > 0 && (
                  <p className="text-rv-orange text-xs mt-1" data-testid="failed-sources-note">
                    {data.failed.length} playlist{data.failed.length > 1 ? "s" : ""} could not be loaded and {data.failed.length > 1 ? "were" : "was"} skipped.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {page === "browse" && (
                  <>
                    <select value={activeUrl} onChange={(e) => loadStations(e.target.value)} className="field w-auto min-w-[220px] cursor-pointer" data-testid="source-select">
                      {allSources.map((l) => (
                        <option key={l.url} value={l.url}>{l.is_global ? "★ " : ""}{l.name}</option>
                      ))}
                    </select>
                    <button onClick={() => loadStations(activeUrl)} disabled={loading} className="btn-primary flex items-center gap-2" data-testid="refresh-stations-button">
                      <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                  </>
                )}
                {page === "recent" && recent.length > 0 && (
                  <button onClick={clear} className="btn-ghost flex items-center gap-2" data-testid="clear-recent-button"><Trash2 size={15} /> Clear</button>
                )}
              </div>
            </div>

            {/* Continue listening */}
            {page === "browse" && recent.length > 0 && !loading && (
              <button onClick={() => playStation(recent[0])} className="card w-full flex items-center gap-4 p-4 mb-6 text-left group hover:border-rv-violet transition-colors" data-testid="continue-listening">
                <div className="w-16 h-16 rounded-xl bg-rv-medium border border-rv-border flex items-center justify-center overflow-hidden shrink-0">
                  {recent[0].logo ? (
                    <img src={logoUrl(recent[0].logo)} alt="" className="max-w-[80%] max-h-[80%] object-contain" onError={(e) => (e.target.style.display = "none")} />
                  ) : (<Radio size={22} className="text-rv-muted" />)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-rv-violet font-bold mb-0.5">Continue listening</div>
                  <div className="font-bold truncate">{recent[0].name}</div>
                  <div className="text-xs text-rv-muted truncate">{recent[0].genre}{recent[0].country ? ` · ${recent[0].country}` : ""}</div>
                </div>
                <span className="btn-primary flex items-center gap-2 shrink-0 group-hover:brightness-110">
                  <Play size={16} fill="currentColor" /> Resume
                </span>
              </button>
            )}

            {/* Filters */}
            {(page === "browse" || filtered.length > 8) && (
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                  <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-rv-muted" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stations…" className="field pl-10" data-testid="station-search-input" />
                </div>
                {page === "browse" && (
                  <>
                    <FilterSelect value={genre} onChange={setGenre} options={["All", ...(data.genres || [])]} icon={Filter} allLabel="All genres" testid="genre-filter" />
                    <FilterSelect value={country} onChange={setCountry} options={["All", ...(data.countries || [])]} icon={Globe} allLabel="All countries" testid="country-filter" />
                    <FilterSelect value={language} onChange={setLanguage} options={["All", ...(data.languages || [])]} icon={Languages} allLabel="All languages" testid="language-filter" />
                    {activeFilters > 0 && (
                      <button onClick={() => { setGenre("All"); setCountry("All"); setLanguage("All"); }} className="btn-ghost text-sm py-2 flex items-center gap-1.5" data-testid="clear-filters-button">
                        <X size={14} /> Clear {activeFilters}
                      </button>
                    )}
                    <span className="text-xs text-rv-muted ml-auto" data-testid="filter-result-count">
                      {filtered.length.toLocaleString()} of {(data.stations || []).length.toLocaleString()}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Content */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-32 text-rv-sub gap-3">
                <Loader2 size={34} className="animate-spin text-rv-violet" />
                <span>Loading stations…</span>
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState page={page} onGoLists={() => setPage("lists")} />
            ) : (
              <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(230px,1fr))]" data-testid="station-grid">
                {filtered.slice(0, 800).map((s) => (
                  <StationCard key={s.uid} s={s} onPlay={playStation} isFav={isFav(s.uid)} onFav={() => toggle(s)} playingUid={playing?.uid} />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {playing && (
        <AudioPlayer
          station={playing}
          onClose={() => setPlaying(null)}
          isFav={isFav(playing.uid)}
          onFav={(s) => toggle(s)}
        />
      )}
    </div>
  );
}

// An ecosystem entry. With an href it becomes a link to the sibling app; without one
// (the current app, or one not shipped yet) it stays inert text.
function EcoItem({ icon: Icon, color, label, note, active, href }) {
  const inner = (
    <>
      <Icon size={15} style={{ color }} />
      <span className="font-semibold text-rv-sub">{label}</span>
      <span className={`ml-auto text-[9px] font-bold uppercase tracking-wider ${active ? "text-rv-green" : "text-rv-muted"}`}>{note}</span>
    </>
  );
  const cls = `flex items-center gap-2.5 text-xs ${active ? "" : "opacity-55"}`;
  if (!href) return <div className={cls}>{inner}</div>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${cls} hover:opacity-100 transition-opacity`}
      data-testid={`eco-link-${label.toLowerCase()}`}
    >
      {inner}
    </a>
  );
}

function FilterSelect({ value, onChange, options, icon: Icon, allLabel, testid }) {
  if (options.length <= 1) return null;
  const active = value !== "All";
  return (
    <div className="relative">
      <Icon size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${active ? "text-rv-cyan" : "text-rv-muted"}`} />
      <select value={value} onChange={(e) => onChange(e.target.value)} className={`field pl-9 pr-8 w-auto max-w-[220px] cursor-pointer appearance-none ${active ? "border-rv-cyan/50 text-rv-cyan" : ""}`} data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o === "All" ? allLabel : o}</option>)}
      </select>
    </div>
  );
}

function StationCard({ s, onPlay, isFav, onFav, playingUid }) {
  const [imgOk, setImgOk] = useState(!!s.logo);
  const isNow = playingUid === s.uid;
  return (
    <div className={`card p-3.5 group hover:border-rv-borderLight hover:bg-rv-hover transition-colors relative ${isNow ? "!border-rv-violet" : ""}`} data-testid="station-card">
      <button onClick={onFav} className={`absolute top-2.5 right-2.5 z-10 p-1.5 rounded-lg transition-colors ${isFav ? "text-rv-yellow" : "text-rv-muted hover:text-rv-yellow"}`} data-testid="favorite-toggle" title={isFav ? "Remove favourite" : "Add favourite"}>
        <Star size={16} fill={isFav ? "currentColor" : "none"} />
      </button>

      <div className="w-full aspect-square rounded-xl bg-rv-medium border border-rv-border flex items-center justify-center overflow-hidden mb-3 relative">
        {imgOk ? (
          <img src={logoUrl(s.logo)} alt="" loading="lazy" decoding="async" className="max-w-[72%] max-h-[72%] object-contain" onError={() => setImgOk(false)} />
        ) : (
          <Music2 size={34} className="text-rv-muted" />
        )}
        <button onClick={() => onPlay(s)} className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="play-station-button">
          <span className="w-12 h-12 rounded-full rv-gradient flex items-center justify-center text-white shadow-lg">
            <Play size={22} fill="currentColor" className="ml-0.5" />
          </span>
        </button>
        {isNow && (
          <span className="absolute bottom-2 left-2 text-[9px] font-bold uppercase tracking-wider bg-rv-violet text-white px-2 py-0.5 rounded-full flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulseDot" /> Live
          </span>
        )}
      </div>

      <div className="font-semibold text-sm truncate" title={s.name}>{s.name}</div>
      <div className="text-xs text-rv-muted truncate mt-0.5">
        {s.genre}{s.country ? ` · ${s.country}` : ""}{s.bitrate ? ` · ${s.bitrate}kbps` : ""}
      </div>
    </div>
  );
}

function EmptyState({ page, onGoLists }) {
  const map = {
    browse: { icon: Radio, title: "No stations to show", sub: "Try a different source, clear filters or refresh." },
    favorites: { icon: Star, title: "No favourites yet", sub: "Tap the star on any station to save it here." },
    recent: { icon: Clock, title: "Nothing played yet", sub: "Stations you play will appear here." },
  };
  const st = map[page];
  return (
    <div className="flex flex-col items-center justify-center py-28 text-center" data-testid="empty-state">
      <div className="w-16 h-16 rounded-2xl bg-rv-card border border-rv-border flex items-center justify-center text-rv-muted mb-4">
        <st.icon size={28} />
      </div>
      <div className="text-lg font-bold">{st.title}</div>
      <div className="text-rv-sub text-sm mt-1 max-w-sm">{st.sub}</div>
      {page === "browse" && (
        <button onClick={onGoLists} className="btn-primary mt-5 flex items-center gap-2"><Plus size={16} /> Add a playlist</button>
      )}
    </div>
  );
}

/* ── My Lists page ─────────────────────────── */
function MyLists({ lists, reload, onActivate, activeUrl }) {
  const [form, setForm] = useState({ name: "", url: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", url: "" });

  const userLists = lists.filter((l) => !l.is_global);

  const add = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await api.post("/lists", { ...form, list_type: "url" }); setForm({ name: "", url: "" }); await reload(); }
    catch (e2) { setErr(errMsg(e2)); }
    finally { setBusy(false); }
  };
  const addPreset = async (p) => {
    setErr(""); setBusy(true);
    try { await api.post("/lists", { name: p.name, url: p.url, list_type: "url" }); await reload(); onActivate(p.url); }
    catch (e2) { setErr(errMsg(e2)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/lists/${id}`); await reload(); }
    catch (e) { setErr(errMsg(e)); }
  };
  const startEdit = (l) => { setErr(""); setEditForm({ name: l.name || "", url: l.url || "" }); setEditingId(l.id); };
  const saveEdit = async (e, l) => {
    e.preventDefault();
    const name = editForm.name.trim(), url = editForm.url.trim();
    if (!name || !url) { setErr("Name and URL are both required"); return; }
    setBusy(true);
    try {
      await api.patch(`/lists/${l.id}`, { name, url });
      setEditingId(null);
      await reload();
      // Keep playing the right thing if the URL of the loaded playlist changed.
      if (activeUrl === l.url && url !== l.url) onActivate(url);
    } catch (e2) { setErr(errMsg(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="animate-fadeIn max-w-4xl">
      <h1 className="text-3xl font-extrabold tracking-tight">My Lists</h1>
      <p className="text-rv-sub text-sm mt-1 mb-6">Add your own radio M3U playlist by URL, or pick a community list below.</p>

      {err && <div className="mb-4 text-sm text-rv-red bg-rv-red/10 border border-rv-red/30 rounded-xl px-4 py-3">{err}</div>}

      <form onSubmit={add} className="card p-5 mb-6 flex flex-col sm:flex-row gap-3" data-testid="add-list-form">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Playlist name" className="field sm:w-52" data-testid="list-name-input" />
        <input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…/radio.m3u" className="field flex-1" data-testid="list-url-input" />
        <button type="submit" disabled={busy} className="btn-primary flex items-center gap-2" data-testid="add-list-button"><Plus size={17} /> Add</button>
      </form>

      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-rv-muted mb-3">Quick add · community radio playlists (GitHub)</div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.url} onClick={() => addPreset(p)} disabled={busy} className="text-xs font-semibold bg-rv-card border border-rv-border hover:border-rv-violet hover:text-rv-cyan text-rv-sub rounded-full px-3.5 py-1.5 transition-colors flex items-center gap-1.5" data-testid="preset-add">
              <Plus size={12} /> {p.name}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3" data-testid="my-lists">
        {userLists.length === 0 ? (
          <div className="card p-8 text-center text-rv-muted">No playlists yet. Add one above or pick a quick playlist. The Global Directory is always available in Browse.</div>
        ) : (
          userLists.map((l) => (
            editingId === l.id ? (
              <form key={l.id} onSubmit={(e) => saveEdit(e, l)} className="card px-5 py-4 flex flex-col sm:flex-row gap-3" data-testid={`edit-my-list-form-${l.id}`}>
                <input autoFocus value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Playlist name" className="field sm:w-52" data-testid={`edit-my-list-name-${l.id}`} />
                <input value={editForm.url} onChange={(e) => setEditForm({ ...editForm, url: e.target.value })} placeholder="https://…/radio.m3u" className="field flex-1" data-testid={`edit-my-list-url-${l.id}`} />
                <div className="flex gap-2">
                  <button type="button" onClick={() => setEditingId(null)} className="btn-ghost px-3">Cancel</button>
                  <button type="submit" disabled={busy} className="btn-primary px-3 disabled:opacity-60" data-testid={`save-my-list-${l.id}`}>{busy ? "Saving…" : "Save"}</button>
                </div>
              </form>
            ) : (
            <div key={l.id} className={`card px-5 py-4 flex items-center justify-between cursor-pointer transition-colors hover:border-rv-borderLight ${activeUrl === l.url ? "!border-rv-violet" : ""}`} onClick={() => onActivate(l.url)} data-testid={`list-item-${l.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-rv-medium border border-rv-border flex items-center justify-center text-rv-cyan shrink-0">
                  <ListMusic size={18} />
                </div>
                <div className="min-w-0">
                  <div className="font-bold truncate">{l.name}</div>
                  <div className="text-rv-muted text-xs truncate max-w-md">{l.url}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={(e) => { e.stopPropagation(); onActivate(l.url); }} className="btn-ghost p-2" title="Load" data-testid={`load-list-${l.id}`}><Play size={15} /></button>
                <button onClick={(e) => { e.stopPropagation(); startEdit(l); }} className="btn-ghost p-2" title="Edit" data-testid={`edit-my-list-${l.id}`}><Pencil size={15} /></button>
                <button onClick={(e) => { e.stopPropagation(); del(l.id); }} className="btn-ghost p-2 hover:!text-rv-red hover:!border-rv-red/40" title="Delete" data-testid={`delete-my-list-${l.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
            )
          ))
        )}
      </div>
    </div>
  );
}
